import React from 'react';
import { X, Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLang } from '../context/LanguageContext';
import { useData } from '../context/DataContext';

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, clear, total, count } = useCart();
  const { t, lang } = useLang();
  const { settings } = useData();

  const T = {
    cart: { en: 'Your Cart', hi: 'आपकी टोकरी' },
    empty: { en: 'Your cart is empty', hi: 'आपकी टोकरी खाली है' },
    emptyHint: { en: 'Add divine products to see them here', hi: 'दिव्य उत्पाद जोड़ें, यहाँ दिखेंगे' },
    subtotal: { en: 'Subtotal', hi: 'कुल' },
    checkoutWhatsapp: { en: 'Order via WhatsApp', hi: 'व्हाट्सएप पर ऑर्डर' },
    clear: { en: 'Clear cart', hi: 'खाली करें' },
  };

  const checkoutWhatsapp = () => {
    if (!items.length) return;
    const lines = items.map(
      (i) => `${lang === 'hi' ? i.name_hi || i.name_en : i.name_en} × ${i.qty} = ₹${i.price * i.qty}`
    );
    const msg =
      `🙏 ${lang === 'hi' ? 'नमस्ते! मैं यह ऑर्डर करना चाहता हूँ' : 'Namaste! I want to order the following'}:\n\n` +
      lines.join('\n') +
      `\n\n${t(T.subtotal)}: ₹${total}`;
    const phone = (settings.whatsapp || '917250412100').replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setOpen(false)}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#FFF9F0] shadow-2xl z-[70] transition-transform duration-300 flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-nv-gold/20 bg-white">
          <h3 className="text-lg font-bold text-nv-wine flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-nv-gold" /> {t(T.cart)}
            {count > 0 && <span className="text-sm text-nv-muted">({count})</span>}
          </h3>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-full hover:bg-nv-gold/10 text-nv-wine"
            data-testid="cart-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingBag className="w-16 h-16 text-nv-gold/30 mb-4" />
            <p className="text-nv-wine font-semibold">{t(T.empty)}</p>
            <p className="text-nv-muted text-sm mt-1">{t(T.emptyHint)}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3 bg-white rounded-xl border border-nv-gold/15 p-3">
                  <img src={i.image} alt="" className="w-16 h-16 rounded-lg object-cover bg-orange-50" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-nv-wine line-clamp-2">
                      {lang === 'hi' ? i.name_hi || i.name_en : i.name_en}
                    </p>
                    <p className="text-nv-gold font-bold text-sm mt-0.5">₹{i.price}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQty(i.id, i.qty - 1)}
                        className="w-7 h-7 rounded-md border border-nv-gold/30 flex items-center justify-center hover:bg-nv-gold/10"
                        data-testid={`cart-dec-${i.id}`}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-sm font-semibold w-6 text-center">{i.qty}</span>
                      <button
                        onClick={() => updateQty(i.id, i.qty + 1)}
                        className="w-7 h-7 rounded-md border border-nv-gold/30 flex items-center justify-center hover:bg-nv-gold/10"
                        data-testid={`cart-inc-${i.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => removeItem(i.id)}
                        className="ml-auto p-1.5 rounded-md text-red-500 hover:bg-red-50"
                        data-testid={`cart-remove-${i.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-nv-gold/20 bg-white px-5 py-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-nv-muted text-sm">{t(T.subtotal)}</span>
                <span className="text-2xl font-bold text-nv-wine" data-testid="cart-subtotal">
                  ₹{total.toLocaleString('en-IN')}
                </span>
              </div>
              <button
                onClick={checkoutWhatsapp}
                className="w-full bg-[#25D366] hover:bg-[#1ea855] text-white font-semibold py-3 rounded-full transition"
                data-testid="cart-checkout-whatsapp"
              >
                {t(T.checkoutWhatsapp)}
              </button>
              <button
                onClick={clear}
                className="w-full text-xs text-nv-muted hover:text-red-500 transition"
                data-testid="cart-clear"
              >
                {t(T.clear)}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
